import { type DataContainer, type Feature, type FeatureStatus, submitMerge } from "@otto-ec/tracking-bct";
import { captureErrorWithPayload } from "utils/error";
import { preventClickBubbling, registerVisibilityObserver } from "utils/events";
import { fetchRetry } from "utils/fetch";
import { createLogger } from "utils/logging";
import { generateRandomString } from "utils/utils";
import { BenefitElement, defineElement } from "../BenefitElement";
import type { HTMLOcTagV1Element } from "@otto-ec/otto-components/tag";

const attributeNameBenefitId = "benefit-id";
const attributeNameFeature = "feature";
const attributeNameTeam = "team";
const attributeNameVariationId = "variation-id";
const attributeNameTargetContextDreson = "target-context-dreson";
const attributeNameCurrentContextDreson = "current-context-dreson";
const shouldSendTracking = "send-tracking";
const attributeNameTrackingParentId = "tracking-parent-id";
const attributeNameSendEvents = "send-events"; /*                  */
const attributeNameRemoveExpired = "remove-expired";
const loadOnVisibility = "load-on-visibility";
const attributeNameData = "data";

const log = createLogger("tag", "tag");
const BENEFIT_TAG_NAME = "ft9-benefit-tag-v1";

const ERROR_KEY = "ft9-benefit-tag-not-ok";
const PROMO_FEATURE_LABEL = "promo_Feature";

export class BenefitTagElement extends BenefitElement {
	protected async onInit(): Promise<void> {
		const data = this.getAttribute(attributeNameData);
		if (data) {
			const success = this.renderFromData(data, true);
			if (success) {
				return;
			}
			log.warn("Failed to render from data attribute, falling back to API call");
		}
		await this.callBackend();
	}

	protected async callBackend() {
		this.replaceChildren(); /*                       */

		if (this.hasAttribute(loadOnVisibility)) {
			registerVisibilityObserver(this, async () => {
				this.removeAttribute(loadOnVisibility);

				await this.callBackend();
			});
			return;
		}

		const targetContextDreson = this.getAttribute(attributeNameTargetContextDreson);
		if (targetContextDreson === "preview-dreson") {
			/*                                                         */
			return;
		}

		if (!targetContextDreson && !this.hasAttribute(attributeNameBenefitId) && !this.hasAttribute(attributeNameVariationId)) {
			log.error("No psr, benefitId nor variationId provided, cannot load tag.");
			captureErrorWithPayload("No psr, benefitId nor variationId provided", "tag", "tag", ERROR_KEY);
			return;
		}

		const urlSearchParams = this.createSearchParams();
		log.info("Calling tag for %s with %s", this.id || "id-not-set", urlSearchParams.toString());

		try {
			const response = await fetchRetry({
				url: `/benefit-tag/tag?${urlSearchParams}`,
				namespace: "tag",
				fetchOptions: {
					method: "GET",
					headers: {
						Accept: "text/html;charset=UTF-8",
						"X-Requested-With": "XMLHttpRequest",
						"X-feature": this.getAttribute(attributeNameFeature) ?? "unset",
						"X-team": this.getAttribute(attributeNameTeam) ?? "unset",
					},
					priority: "auto",
				},
				retryOptions: {
					limit: 2,
					delay: 50,
				},
			});

			if (!response.ok) {
				log.error("tag API endpoint returns a non ok response!", response.status);
				captureErrorWithPayload("fetchAndRender error", "tag", "tag", ERROR_KEY, response?.status);
				return;
			}

			const data = await response.text();
			this.renderFromData(data, false);
		} catch (error) {
			log.error("tag API request returns an error!", error);
			captureErrorWithPayload("fetchAndRender error", "tag", "tag", ERROR_KEY, error);
		}
	}

	protected createSearchParams() {
		return this.buildSearchParams({
			benefitId: this.getAttribute(attributeNameBenefitId),
			originFeature: this.getAttribute(attributeNameFeature), /*                                    */
			team: this.getAttribute(attributeNameTeam), /*                                    */
			variationId: this.getAttribute(attributeNameVariationId),
			targetContextPsr: this.getAttribute(attributeNameTargetContextDreson),
		});
	}

	private renderFromData(data: string, encoded: boolean): boolean {
		/*                                                                                                          */
		function decodeBase64AsUtf8(base64: string): string | null {
			try {
				const binaryString = atob(base64);
				const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
				return new TextDecoder().decode(bytes);
			} catch (error) {
				log.error("Failed to decode data attribute", error);
				captureErrorWithPayload("data attribute decode error", "tag", "tag", ERROR_KEY, error);
				return null;
			}
		}

		const decodedHtml = encoded ? decodeBase64AsUtf8(data) : data;
		if (!decodedHtml) {
			return false;
		}

		const template = document.createElement("template");
		template.innerHTML = decodedHtml;

		const tag = template.content.firstElementChild;
		if (!(tag instanceof HTMLElement) || tag.localName !== "oc-tag-v1") {
			log.warn("No valid root element in response");
			return false;
		}
		const ocTag = tag as HTMLOcTagV1Element;

		const benefitId = ocTag.dataset.benefitId;
		if (benefitId && this.getAttribute(attributeNameFeature) && !ocTag.getAttribute("data-oc-sheet-v1-create.base64-url")) {
			/*                                        */
			const sheetUrl = this.buildSheetUrl(benefitId);
			ocTag.setAttribute("data-oc-sheet-v1-create.base64-url", btoa(sheetUrl));
		}

		/*                                                           */
		/*                                                                                                              */
		/*                                                                                                                      */
		/*                                                                         */
		/*                                                                                                             */
		/*                                              */
		if (this.getAttribute("slot") === "benefit-tag") {
			const pricingElement = this.closest("ofc-pricing-item-v1[display-size]");
			if (pricingElement) {
				const size = pricingElement.getAttribute("display-size") ?? "100";
				if (size === "200") {
					ocTag.setAttribute("size", "50");
				} else {
					ocTag.setAttribute("size", "25");
				}
			}
		}

		this.replaceChildren(...template.content.childNodes);

		this.initTag(ocTag);
		preventClickBubbling(this);
		return true;
	}

	private buildSheetUrl(benefitId: string): string {
		/*                                                                                              */
		/*                                                                                       */
		const originFeature = this.getAttribute(attributeNameFeature) ?? "";
		let sheetUrl = `/benefit-sheet/sheet/${encodeURIComponent(originFeature)}/${encodeURIComponent(benefitId)}?openTriggeredBy=ClickOnTag`;

		const targetContextPsr = this.getAttribute(attributeNameTargetContextDreson);
		const currentContextPsr = this.getAttribute(attributeNameCurrentContextDreson);
		if (targetContextPsr) {
			sheetUrl += `&contextPsr=${encodeURIComponent(targetContextPsr)}`;
		} else if (currentContextPsr) {
			sheetUrl += `&contextPsr=${encodeURIComponent(currentContextPsr)}`;
		}

		return sheetUrl;
	}

	private initTag(tag: HTMLOcTagV1Element) {
		/*                                              */
		if (this.hasAttribute(attributeNameRemoveExpired) && tag.dataset.expirationDate) {
			this.triggerReloadAt(tag.dataset.expirationDate);
		}

		/*              */
		const trackingParentId = this.getAttribute(attributeNameTrackingParentId);
		this.sendTracking(tag, trackingParentId);
	}

	private sendTracking(tag: HTMLElement, trackingParentId: string | null) {
		if (!this.hasAttribute(shouldSendTracking)) {
			return;
		}

		const trackingData = tag.dataset.trackingLabels;
		if (!trackingData) {
			return;
		}

		let labels = JSON.parse(trackingData) as DataContainer;
		if (trackingParentId) {
			const { [PROMO_FEATURE_LABEL]: _promoFeature, ...remainingLabels } = labels;
			labels = remainingLabels;
		}

		let feature = {
			id: generateRandomString(20),
			name: "Benefit",
			status: "loaded" as FeatureStatus,
			labels,
		} as Feature;

		if (trackingParentId) {
			feature = {
				...feature,
				parentId: trackingParentId,
			};
		}

		submitMerge({}, [feature]);
	}
}

defineElement(BenefitTagElement, BENEFIT_TAG_NAME, "ftnine_emergency_custom_element_disable_tag");
